import React, { useEffect, useState } from 'react';
import { IState, ReduxMap } from '../../redux/redux';
import { store } from '../../redux/oldStore';
import { IMobileAppInfoes, IFmsApp, IAppScreen } from './MobileAppTypes';
import { toast } from 'react-toastify';
import Button from '../_Core/Button';
import Modal from '../Modal/Modal';
import MobileAppEditor from './MobileAppEditor';
import MobileAppScreenManager from './MobileAppScreenManager';
import Icon, { IconType } from '../Icon/Icon';
import { sanitizeHtml } from '../../utils/Tools';
import { ServerResponse } from '../../utils/Server';
import { FormatDate } from '../../utils/Tools';
import { UiValuePairs } from '../../utils/CommonTypes/UserTypes';
import type { IfilePickerResponse } from '../GenericForms/FilePicker';

interface IProps extends IState {}

const MobileAppManager: React.FC<IProps> = (props: IProps) => {
  const [appData, setAppData] = useState<IMobileAppInfoes | null>(null);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [editingApp, setEditingApp] = useState<IFmsApp | undefined>(undefined);
  const [showScreenManager, setShowScreenManager] = useState<boolean>(false);
  const [managingScreensForApp, setManagingScreensForApp] = useState<IFmsApp | undefined>(undefined);
  const [existingClientFiles, setExistingClientFiles] = useState<UiValuePairs[]>([]);
  useEffect(() => {}, []);

  useEffect(() => {
    if (props.User) {
      loadAppConfigs();
      LoadClientFiles();
    }
  }, [props.User]);

  const loadAppConfigs = () => {
    // call ../MobileApp/Info IMobileAppInfoes to get app configurations
    store.server.getApi2<IMobileAppInfoes>('../MobileApp/Info').then((x) => {
      if (x.Success) {
        setAppData(x.Value);
        setManagingScreensForApp((prev) => (prev ? x.Value.Apps.find((a) => a.PublicId === prev.PublicId) || prev : prev));
      } else {
        toast.error('Failed to load app configurations. ' + x.Message);
        console.error('Failed to load app configurations');
        console.error(x.Message);
      }
    });
  };

    const LoadClientFiles = () => {
      store.server.getApi<ServerResponse<IfilePickerResponse>>(`../file/clientFiles`).then((x) => {
        if (x.Success) {
          let fileList: UiValuePairs[] = [];
          x.Value.files.forEach((file) => {
            fileList.push({ Value: file.Value || '', Name: file.Name || '' });
          });
          setExistingClientFiles(fileList);
        } else {
          toast.warning(x.Message);
        }
      });
    };

  const handleAddApp = () => {
    setEditingApp(undefined);
    setShowEditor(true);
  };

  const handleEditApp = (app: IFmsApp) => {
    setEditingApp(app);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingApp(undefined);
  };

  const handleSaveApp = (app: IFmsApp, fileDic: Record<string, File>) => {
    
    let files: File[] = [];
    let fileMap: Record<string, string> = {};
    for (const key in fileDic) {
      fileMap[key] = fileDic[key].name;
      files.push(fileDic[key]);
    }

    let data = {...app, Screens:undefined,fileMap};
    console.log('Saving app data: ', data);

    store.server.postApi<ServerResponse<IFmsApp>>('../MobileApp/Save', { jsonData: JSON.stringify(data) }, files).then((x) => {
      if (x.Success) {
        toast.success('App saved successfully!');
        loadAppConfigs(); // Reload the data
        handleCloseEditor();
      } else {
        toast.error('Failed to save app. ' + x.Message);
        console.error('Failed to save app');
        console.error(x.Message);
      }
    });
  };

  const handleRemoveApp = (app: IFmsApp) => {
    if (window.confirm('Are you sure you want to remove this app?')) {
      store.server.postApi<ServerResponse<IFmsApp>>('../MobileApp/Remove', app).then((x) => {
        if (x.Success) {
          toast.success('App removed successfully!');
          loadAppConfigs(); // Reload the data
          handleCloseEditor();
        } else {
          toast.error('Failed to remove app. ' + x.Message);
          console.error('Failed to remove app');
          console.error(x.Message);
        }
      });
    }
  };

  const handleSaveScreen = (app: IFmsApp, screen: IAppScreen, fileDic: Record<string, File>): Promise<boolean> => {


    let files: File[] = [];
    let fileMap: Record<string, string> = {};
    for (const key in fileDic) {
      fileMap[key] = fileDic[key].name;
      files.push(fileDic[key]);
    }

    let data = {...screen, fileMap};
    console.log('Saving screen data: ', data);

    return store.server.postApi<ServerResponse<IAppScreen>>(`../MobileApp/SaveScreen/${app.PublicId}`, { jsonData: JSON.stringify(data) }, files)
    .then((x) => {
      if (x.Success) {
        toast.success('Screen saved successfully!');
        loadAppConfigs(); // Refreshes appData and managingScreensForApp so the Screens list shows the saved screen's PublicId without a full re-open
        return true;
      } else {
        toast.error('Failed to save screen. ' + x.Message);
        console.error('Failed to save screen');
        console.error(x.Message);
        return false;
      }
    });
  }

  const handleRemoveScreen = (app: IFmsApp, screen: IAppScreen) => {
    store.server.postApi<ServerResponse<IAppScreen>>(`../MobileApp/RemoveScreen`, { appId: app.PublicId, screenId: screen.PublicId })
    .then((x) => {
      if (x.Success) {
        toast.success('Screen removed successfully!');
        loadAppConfigs();
        // Note: Screen editor will close automatically when screen is removed - this is expected behavior
        handleScreenManagerClose();
      } else {
        toast.error('Failed to remove screen. ' + x.Message);
        console.error('Failed to remove screen');
        console.error(x.Message);
      }
    });
  }

  const handleManageScreens = (app: IFmsApp) => {
    setManagingScreensForApp(app);
    setShowScreenManager(true);
  };

  const handleScreenManagerClose = () => {
    setShowScreenManager(false);
    setManagingScreensForApp(undefined);
  };

  return (
    <>
      <div className="flex-between">
        <h3>Mobile App Manager</h3>
        <div>
          <Button type='add' text="Add New App" onClick={handleAddApp} />
        </div>
      </div>

      {appData && appData.Apps.length > 0 ? (
        <div className="item-list">
          {appData.Apps.map((app) => (
            <div key={app.Id} className="list-item mobile-app-item">
              <div className="flex-between">
                <div style={{ flex: 1 }}>
                  <div className="flex" style={{ alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                    <h4 style={{ margin: 0 }}>{app.Name}</h4>
                    {app.AppShortkey && (
                      <span className="badge" style={{ backgroundColor: '#007bff', color: 'white', fontSize: '0.7em' }}>
                        {app.AppShortkey}
                      </span>
                    )}
                    {!app.IsActive && (
                      <span className="badge" style={{ backgroundColor: '#999', color: 'white', fontSize: '0.7em' }}>
                        Inactive
                      </span>
                    )}
                  </div>
                  {app.Description && <div dangerouslySetInnerHTML={{__html:sanitizeHtml(app.Description)}}></div>}
                  
                  <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                    <Icon type={IconType.phone} /> {app.Screens?.length || 0} screen{(app.Screens?.length || 0) !== 1 ? 's' : ''} configured
                  </div>
                  <div className='small'>
                    <Icon type={IconType.calendar} />
                    {FormatDate(app.LastUpdateAtUtc)} (Last Updated)
                  </div>
                </div>
                <div className="btn-group" style={{ gap: '5px' }}>
                    <Button 
                      type='edit' 
                      text="Screens"
                      addClass='btn'
                      extraSmall
                      onClick={() => handleManageScreens(app)}
                    />
                    <Button 
                      type='edit' 
                      text="Settings"
                      addClass='btn-secondary'
                      extraSmall
                      onClick={() => handleEditApp(app)}
                    />
                    <Button
                      type='details'
                      text="Notifications"
                      addClass='btn-secondary'
                      extraSmall
                      onClick={() => {
                        window.location.href = `/app/admin/mobile-app/${app.PublicId}`;
                      }}
                    />
                    <Button
                      type='details'
                      text="Banners"
                      addClass='btn-secondary'
                      extraSmall
                      onClick={() => {
                        window.location.href = `/app/admin/mobile-app/${app.PublicId}?tab=banners`;
                      }}
                    />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="alert alert-info">No mobile apps configured yet. Click "Add New App" to get started.</div>
      )}
      
      {showEditor && (
        <Modal 
          setModalOpen={setShowEditor} 
          title={editingApp ? `Edit ${editingApp.Name || 'Mobile App'}` : 'Add New Mobile App'} 
          size="xxl"
        >
          <MobileAppEditor
            state={props}
            app={editingApp}
            onSave={handleSaveApp}
            onClose={handleCloseEditor}
            onRemove={handleRemoveApp}
          />
        </Modal>
      )}
      
      {showScreenManager && managingScreensForApp && (
        <MobileAppScreenManager
          app={managingScreensForApp}
          show={showScreenManager}
          onClose={handleScreenManagerClose}
          onSave={handleSaveScreen}
          onRemove={handleRemoveScreen}
          existingClientFiles={existingClientFiles}
        />
      )}
    </>
  );
};


export default ReduxMap(MobileAppManager);